import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableCheckboxList } from "@/components/ui/searchable-checkbox-list";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import { Switch } from "@/components/ui/switch";
import {
  getQuestionGroupJurisdictions,
  getQuestionGroupsOperatorLicenseTypesLicenseTypeIdQuestionGroupsGet,
  updateQuestionGroupJurisdictions,
  updateQuestionGroupOperatorQuestionGroupsGroupIdPatch,
} from "@/hera-api/client";
import {
  getAllLicenseTypeQuestionsOptions,
  getJurisdictionsOptions,
  getLicenseTypesOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import type { QuestionType } from "@/hera-api/client/types.gen";
import { getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { QuestionGroupFlowDesignerButton } from "./question-flow-designer";

interface QuestionGroupDetailsSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: string | null;
  groupType: QuestionType;
}

export function QuestionGroupDetailsSheet({
  isOpen,
  onOpenChange,
  groupId,
  groupType,
}: QuestionGroupDetailsSheetProps) {
  const [name, setName] = useState("");
  const [licenseTypeName, setLicenseTypeName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [selectedJurisdictionIds, setSelectedJurisdictionIds] = useState<
    string[]
  >([]);

  const queryClient = useQueryClient();

  // Fetch all license types to get the name
  const { data: licenseTypesData } = useQuery({
    ...getLicenseTypesOptions(),
  });

  // Fetch question group data
  const { data: groupsData, isLoading } = useQuery({
    queryKey: ["questionGroups", groupType, groupId] as const,
    queryFn: async () => {
      if (!groupId) return null;

      const licenseTypes = licenseTypesData?.license_types || [];
      for (const lt of licenseTypes) {
        const response =
          await getQuestionGroupsOperatorLicenseTypesLicenseTypeIdQuestionGroupsGet(
            {
              path: { license_type_id: lt.id },
              query: { question_type: groupType },
            },
          );

        const group = response.data?.find((g) => g.id === groupId);
        if (group) {
          return group;
        }
      }
      return null;
    },
    enabled: !!groupId && !!licenseTypesData,
  });

  const { data: questionsData } = useQuery({
    ...getAllLicenseTypeQuestionsOptions({
      query: { include_deleted: true },
    }),
    enabled: isOpen && !!groupId,
  });

  const { data: allJurisdictionsData } = useQuery({
    ...getJurisdictionsOptions(),
    enabled: isOpen && !!groupId,
  });

  // Update form when data is loaded
  useEffect(() => {
    if (groupsData) {
      setName(groupsData.name || "");
      setIsActive(groupsData.deleted_at === null);
      const licenseType = licenseTypesData?.license_types?.find(
        (lt) => lt.id === groupsData.license_type_id,
      );
      setLicenseTypeName(licenseType?.name || "");
    }
  }, [groupsData, licenseTypesData]);

  // Jurisdictions configured for this group's license type, flagged as assigned
  const { data: jurisdictionsData, isLoading: isLoadingJurisdictions } =
    useQuery({
      queryKey: ["questionGroupJurisdictions", groupId] as const,
      queryFn: async () => {
        if (!groupId) return null;
        const response = await getQuestionGroupJurisdictions({
          path: { group_id: groupId },
        });
        return response.data ?? null;
      },
      enabled: !!groupId && isOpen,
    });

  const jurisdictions = useMemo(
    () => jurisdictionsData?.jurisdictions ?? [],
    [jurisdictionsData],
  );

  const assignedJurisdictionIds = useMemo(
    () =>
      jurisdictions
        .filter((jurisdiction) => jurisdiction.assigned)
        .map((jurisdiction) => jurisdiction.jurisdiction_id),
    [jurisdictions],
  );

  // Reset pending assignment edits whenever fresh server state arrives
  useEffect(() => {
    setSelectedJurisdictionIds(assignedJurisdictionIds);
  }, [assignedJurisdictionIds]);

  const jurisdictionItems = useMemo(
    () =>
      jurisdictions.map((jurisdiction) => ({
        id: jurisdiction.jurisdiction_id,
        label: jurisdiction.jurisdiction_name,
        description: [jurisdiction.county, jurisdiction.state]
          .filter(Boolean)
          .join(", "),
      })),
    [jurisdictions],
  );

  const toggleJurisdiction = (jurisdictionId: string, checked: boolean) => {
    setSelectedJurisdictionIds((current) =>
      checked
        ? current.includes(jurisdictionId)
          ? current
          : [...current, jurisdictionId]
        : current.filter((id) => id !== jurisdictionId),
    );
  };

  const updateMutation = useMutation({
    mutationFn: async ({
      groupId,
      name,
      deleted_at,
      jurisdictionIds,
    }: {
      groupId: string;
      name: string;
      deleted_at: string | null;
      jurisdictionIds: string[] | null;
    }) => {
      const result =
        await updateQuestionGroupOperatorQuestionGroupsGroupIdPatch({
          path: { group_id: groupId },
          body: { name, deleted_at },
        });
      if (jurisdictionIds) {
        await updateQuestionGroupJurisdictions({
          path: { group_id: groupId },
          body: { jurisdiction_ids: jurisdictionIds },
        });
      }
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["questionGroupJurisdictions"],
      });
      queryClient.invalidateQueries({
        queryKey: ["questionGroupsByType"],
      });
      queryClient.invalidateQueries({
        queryKey: ["allRegistrationQuestionGroups"],
      });
      queryClient.invalidateQueries({
        queryKey: ["allInspectionQuestionGroups"],
      });
      queryClient.invalidateQueries({
        queryKey: ["allDeregistrationQuestionGroups"],
      });
      queryClient.invalidateQueries({
        queryKey: ["questionGroups"],
      });
      toast({
        title: "Success",
        description: "Question group updated successfully",
      });
      onOpenChange(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update question group: ${getQueryErrorMessage(error)}`,
        variant: "destructive",
      });
    },
  });

  const hasJurisdictionChanges =
    selectedJurisdictionIds.length !== assignedJurisdictionIds.length ||
    selectedJurisdictionIds.some((id) => !assignedJurisdictionIds.includes(id));

  const handleSubmit = () => {
    if (!groupId || !name.trim()) return;

    const deleted_at = isActive ? null : new Date().toISOString();
    updateMutation.mutate({
      groupId,
      name,
      deleted_at,
      jurisdictionIds: hasJurisdictionChanges ? selectedJurisdictionIds : null,
    });
  };

  const handleCancel = () => {
    setSelectedJurisdictionIds(assignedJurisdictionIds);
    onOpenChange(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>Edit Question Group</SheetTitle>
          <SheetDescription>
            Edit the question group name and status.
          </SheetDescription>
        </SheetHeader>
        <div className="overflow-y-auto max-h-[calc(100vh-120px)] px-4 py-6">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              Loading...
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex justify-end">
                {groupId ? (
                  <QuestionGroupFlowDesignerButton
                    questionType={groupType}
                    questionGroupId={groupId}
                    allQuestions={questionsData?.questions ?? []}
                    jurisdictions={allJurisdictionsData?.jurisdictions ?? []}
                    title={`${name || "Question group"} flow`}
                    onSaved={() => {
                      void queryClient.invalidateQueries({
                        queryKey: getAllLicenseTypeQuestionsOptions({
                          query: { include_deleted: true },
                        }).queryKey,
                      });
                    }}
                  />
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="license-type">License Type</Label>
                <Input
                  id="license-type"
                  value={licenseTypeName}
                  disabled
                  className="bg-muted"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="group-name">Group Name</Label>
                <Input
                  id="group-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter group name"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && name.trim()) {
                      handleSubmit();
                    }
                  }}
                />
              </div>

              <div className="flex items-center justify-between space-x-2">
                <div className="space-y-0.5">
                  <Label htmlFor="active-toggle">Active</Label>
                  <div className="text-sm text-muted-foreground">
                    {isActive ? "Group is active" : "Group is inactive"}
                  </div>
                </div>
                <Switch
                  id="active-toggle"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
              </div>

              <div className="space-y-2">
                <Label>Jurisdictions</Label>
                <div className="text-sm text-muted-foreground">
                  Jurisdictions configured for this license type. Tick a
                  jurisdiction to give it this group, untick to remove it.
                </div>
                {isLoadingJurisdictions ? (
                  <div className="rounded-md border py-4 text-center text-sm text-muted-foreground">
                    Loading jurisdictions...
                  </div>
                ) : (
                  <SearchableCheckboxList
                    items={jurisdictionItems}
                    selectedIds={selectedJurisdictionIds}
                    onToggle={toggleJurisdiction}
                    onClear={() => setSelectedJurisdictionIds([])}
                    summary={
                      selectedJurisdictionIds.length === 0
                        ? "No jurisdictions selected"
                        : `${selectedJurisdictionIds.length} jurisdiction(s) selected`
                    }
                    clearLabel="Clear jurisdictions"
                    searchPlaceholder="Search jurisdictions..."
                    noItemsMessage="No jurisdictions are configured for this license type."
                  />
                )}
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!name.trim() || updateMutation.isPending}
                  className="flex-1"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {updateMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={updateMutation.isPending}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
